import { motion } from "framer-motion";
import { BookOpen, CheckCircle2, ArrowRight, Phone } from "lucide-react";
import SEO from "../components/SEO";
import { CONTACT } from "../components/Footer";

const INCLUDED = [
  "Bank & credit card reconciliation",
  "Monthly profit & loss statements",
  "Accounts payable & receivable tracking",
  "QuickBooks & Xero support",
  "Receipt & expense organization",
  "Year-end books ready for tax filing",
];

export default function Bookkeeping({ onBookAppointment }) {
  return (
    <div data-testid="bookkeeping-page" className="pt-16">
      <SEO path="/marizema-bookkeeping-service"
        title="Marizema Bookkeeping Service | Small Business Bookkeeping in Tampa, FL"
        description="Reliable monthly bookkeeping for small businesses in Tampa, FL — reconciliations, profit & loss statements, QuickBooks support and tax-ready books." />
      <section className="relative py-20 sm:py-28 overflow-hidden bg-[#0B2545]">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1626266061368-46a8f578ddd6?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Organized bookkeeping at a desk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/92 to-[#0B2545]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-3 flex items-center gap-2">
              <BookOpen size={14} /> Marizema Bookkeeping Service
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              Never stress about messy books again
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed">
              We turn your receipts, invoices, and bank statements into organized financial clarity — accurate, timely, and professional service you can trust.
            </p>
            <button data-testid="bookkeeping-cta-book" onClick={onBookAppointment}
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
              Book a Bookkeeping Consultation <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-[#0B2545]">What's included every month</h2>
            <p className="mt-3 text-[#1F2933]/80 leading-relaxed">
              Reliable bookkeeping that helps small businesses and individuals stay organized, manage finances, and make smarter decisions.
            </p>
            <ul className="mt-6 space-y-3">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-[#1F2933]/85">
                  <CheckCircle2 size={17} className="text-[#86680A] mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#0B2545]">Pricing built around your business</h3>
            <p className="mt-3 text-sm text-[#1F2933]/80 leading-relaxed">
              Every business is different. Pricing depends on your transaction volume and specific needs,
              so we provide an exact quote after a quick conversation — no obligation.
            </p>
            <div className="mt-6 space-y-3">
              <button data-testid="bookkeeping-quote-book" onClick={onBookAppointment}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
                Book a Free Consultation <ArrowRight size={15} />
              </button>
              <a data-testid="bookkeeping-quote-call" href={CONTACT.phoneTel}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[#0B2545]/40 text-[#0B2545] text-sm font-bold hover:bg-[#0B2545]/5 transition-colors">
                <Phone size={15} /> Call {CONTACT.phoneDisplay}
              </a>
            </div>
            <p className="mt-5 text-xs text-[#1F2933]/75">
              Prefer email? Reach us at <a href={`mailto:${CONTACT.email}`} className="text-[#86680A] underline">{CONTACT.email}</a> — we reply within one business day.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#14375E] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold text-white">Get your books tax-ready, year-round</h2>
          <p className="mt-3 text-slate-300">Talk to our Tampa team about a monthly bookkeeping plan that fits your business.</p>
          <button data-testid="bookkeeping-cta-bottom" onClick={onBookAppointment}
            className="mt-7 px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
            Book Appointment
          </button>
        </div>
      </section>
    </div>
  );
}
